/**
 * Structured bilingual (EN / HE) Chaos Machine WING crafting guides for MU BEGINNING.
 * Player-facing only — no internal item / mix / index numbers in the rendered text.
 * Based on the server's native wing combines (first wings, second wings + capes, third wings).
 *
 * Topics: "wings-1" (first wings), "wings-2" (second wings + Dark Lord / Rage Fighter capes),
 * "wings-3" (third wings + capes).
 *
 * Block types consumed by Guides.jsx:
 *   p | h | note | warn · table · npc · itemcard · mix · timeline · results
 */

import { guideImg } from "./guideAssets";

/** Chaos Goblin — opens the Chaos Machine. Shared across every wing guide. */
const chaosGoblinNpc = () => ({
  type: "npc",
  img: guideImg.chaosGoblin,
  name: "Chaos Goblin",
  map: { en: "Noria", he: "Noria" },
  coords: "180, 103",
  note: {
    en: "Also stands in Arkania (232, 57). Talk to the Chaos Goblin to open the Chaos Machine — every wing recipe below is combined here.",
    he: "עומד גם ב־Arkania (232, 57). דברו עם ה־Chaos Goblin כדי לפתוח את ה־Chaos Machine — כל מתכוני הכנפיים למטה משולבים כאן.",
  },
});

/** Luck + account success bonus explanation. Shared across every wing guide. */
const luckVipNotes = () => [
  {
    type: "note",
    en: "Account SUCCESS BONUS is separate from the configured base mix rate. Wing 1 and Wing 2 use a base rate of 60% for Normal, Bronze, Silver, and Gold alike. Then the account bonus is added: Normal +0 · Bronze +5 · Silver +10 · Gold +15 → final before Luck: Normal 60% · Bronze 65% · Silver 70% · Gold 75%.",
    he: "בונוס ההצלחה ברמת החשבון נפרד משיעור הבסיס המוגדר לשילוב. לכנפיים 1 ו־2 שיעור הבסיס הוא 60% ל־Normal, Bronze, Silver ו־Gold כאחד. ואז מתווסף בונוס החשבון: Normal +0 · Bronze +5 · Silver +10 · Gold +15 → לפני Luck: Normal 60% · Bronze 65% · Silver 70% · Gold 75%.",
  },
  {
    type: "note",
    en: "Luck (Talisman of Luck) adds on top of that total. The combined success chance is capped at 100%.",
    he: "Luck (Talisman of Luck) מתווסף מעל הסכום הזה. סיכוי ההצלחה המשולב מוגבל ל־100%.",
  },
];

/** Standard failure warning for every Chaos Machine wing combine. */
const failWarn = () => ({
  type: "warn",
  en: "If the combine fails, everything placed in the Chaos Machine box is destroyed. Only add materials you can afford to lose.",
  he: "אם השילוב נכשל, כל מה שהונח בקופסת ה־Chaos Machine נהרס. הוסיפו רק חומרים שאתם מוכנים לאבד.",
});

const FAIL_CELL = {
  en: "Failure destroys the whole box.",
  he: "כישלון הורס את כל הקופסה.",
};

export const guideWingsContent = {
  "wings-1": [
    {
      type: "h",
      en: "First Wings (Level 1)",
      he: "כנפיים ראשונות (Level 1)",
    },
    {
      type: "p",
      en: "Level 1 wings are built in the Chaos Machine across a few steps: first craft a Chaos Weapon, upgrade it to +4 with Additional Option, then combine that weapon with a Jewel of Chaos to create your wing.",
      he: "כנפיים ברמה 1 נבנות ב־Chaos Machine בכמה שלבים: קודם יוצרים Chaos Weapon, משדרגים אותו ל־+4 עם Additional Option, ואז משלבים את הנשק עם Jewel of Chaos כדי ליצור את הכנף.",
    },
    chaosGoblinNpc(),
    ...luckVipNotes(),
    failWarn(),

    {
      type: "h",
      en: "Step 1 — Craft a Chaos Weapon",
      he: "שלב 1 — יצירת Chaos Weapon",
    },
    {
      type: "p",
      en: "Combine a base weapon with a Jewel of Chaos to create a random Chaos Weapon. You raise the success rate with a +4 weapon that has Additional Option ≥ 1, and/or by adding extra Jewels of Bless / Soul as a money pool.",
      he: "שלבו נשק בסיס עם Jewel of Chaos כדי ליצור Chaos Weapon אקראי. מעלים את שיעור ההצלחה עם נשק +4 שיש לו Additional Option ≥ 1, ו/או בהוספת Jewel of Bless / Soul נוספים כמאגר כסף.",
    },
    {
      type: "mix",
      title: { en: "Chaos Weapon", he: "Chaos Weapon" },
      zen: {
        en: "Zen = (rate after Luck) × 10,000 (+ small castle tax may apply). Account bonus does NOT change Zen.",
        he: "Zen = (שיעור אחרי Luck) × 10,000 (ייתכן מס טירה קטן). בונוס החשבון אינו משנה את ה־Zen.",
      },
      rate: {
        en: "Order: Base = (total item money value ÷ 20,000)% → Luck adds next (affects Zen) → Zen calculated → THEN account bonus Normal +0 · Bronze +5 · Silver +10 · Gold +15 applies to the roll only. Cap 100%.",
        he: "סדר: בסיס = (ערך הכסף הכולל של הפריטים ÷ 20,000)% → אחר כך Luck (משפיע על Zen) → חישוב Zen → ואז בונוס החשבון Normal +0 · Bronze +5 · Silver +10 · Gold +15 חל על הגלגול בלבד. תקרה 100%.",
      },
      fail: FAIL_CELL,
      ingredients: [
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×1+" },
        {
          name: {
            en: "A +4 weapon with Additional Option ≥ 1",
            he: "נשק +4 עם Additional Option ≥ 1",
          },
          level: "+4",
          option: "≥1",
          note: {
            en: "The base item that becomes the Chaos Weapon. Higher item value = higher rate.",
            he: "פריט הבסיס שהופך ל־Chaos Weapon. ערך פריט גבוה יותר = שיעור גבוה יותר.",
          },
        },
        {
          img: guideImg.jewelBless,
          name: "Jewel of Bless",
          note: { en: "Optional — money pool", he: "אופציונלי — מאגר כסף" },
        },
        {
          img: guideImg.jewelSoul,
          name: "Jewel of Soul",
          note: { en: "Optional — money pool", he: "אופציונלי — מאגר כסף" },
        },
      ],
      results: [
        { img: guideImg.chaosAxe, name: "Chaos Dragon Axe" },
        { img: guideImg.chaosBow, name: "Chaos Nature Bow" },
        { img: guideImg.chaosStaff, name: "Chaos Lighting Staff" },
      ],
    },
    {
      type: "note",
      en: "All three Chaos Weapons have an equal chance — you cannot choose which one you get. Any of them can be used for the wing step.",
      he: "לשלושת ה־Chaos Weapons יש סיכוי שווה — אי אפשר לבחור איזה מקבלים. כל אחד מהם מתאים לשלב הכנף.",
    },

    {
      type: "h",
      en: "Step 2 — Prepare the Chaos Weapon",
      he: "שלב 2 — הכנת ה־Chaos Weapon",
    },
    {
      type: "p",
      en: "Before making a wing, your Chaos Weapon must be Level ≥ 4 and carry Additional Option ≥ 1. Upgrade the level with Jewel of Bless / Soul and add Additional Option first.",
      he: "לפני יצירת כנף, ה־Chaos Weapon חייב להיות Level ≥ 4 ולשאת Additional Option ≥ 1. שדרגו את הרמה עם Jewel of Bless / Soul והוסיפו Additional Option קודם.",
    },
    {
      type: "itemcard",
      img: guideImg.chaosAxe,
      name: {
        en: "Chaos Weapon (Axe / Bow / Staff)",
        he: "Chaos Weapon (Axe / Bow / Staff)",
      },
      level: "+4",
      option: "≥1",
      note: {
        en: "Required input for the wing combine below.",
        he: "פריט חובה לשילוב הכנף למטה.",
      },
    },

    {
      type: "h",
      en: "Step 3 — Craft the Wing",
      he: "שלב 3 — יצירת הכנף",
    },
    {
      type: "p",
      en: "Combine a prepared Chaos Weapon +4 (Option ≥ 1) with a Jewel of Chaos to create your first wing. On this server the base success rate for this combine is fixed at 60%.",
      he: "שלבו Chaos Weapon +4 מוכן (Option ≥ 1) עם Jewel of Chaos כדי ליצור את הכנף הראשונה. בשרת הזה שיעור ההצלחה הבסיסי לשילוב הזה קבוע על 60%.",
    },
    {
      type: "mix",
      title: { en: "Wings (Level 1)", he: "Wings (Level 1)" },
      zen: {
        en: "Zen = (rate after Luck) × 10,000 (+ small castle tax may apply). Account bonus does NOT change Zen.",
        he: "Zen = (שיעור אחרי Luck) × 10,000 (ייתכן מס טירה קטן). בונוס החשבון אינו משנה את ה־Zen.",
      },
      rate: {
        en: "Order: Base 60% (all account levels) → Luck applied next (this affects Zen) → Zen = (rate after Luck) × 10,000 → THEN account bonus applies to the roll chance only. Example: Normal no Luck → 60% roll, 600,000 Zen. Gold no Luck → 75% roll, still 600,000 Zen. Normal +10 Luck → 70% roll, 700,000 Zen. Cap 100%.",
        he: "סדר: בסיס 60% (כל רמות החשבון) → אחר כך Luck (משפיע על Zen) → Zen = (שיעור אחרי Luck) × 10,000 → ואז בונוס החשבון חל רק על סיכוי הגלגול. דוגמה: Normal בלי Luck → גלגול 60%, 600,000 Zen. Gold בלי Luck → גלגול 75%, עדיין 600,000 Zen. Normal +10 Luck → גלגול 70%, 700,000 Zen. תקרה 100%.",
      },
      fail: FAIL_CELL,
      ingredients: [
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×1+" },
        {
          img: guideImg.chaosAxe,
          name: {
            en: "Chaos Weapon (Axe / Bow / Staff)",
            he: "Chaos Weapon (Axe / Bow / Staff)",
          },
          level: "+4",
          option: "≥1",
        },
        {
          name: {
            en: "Extra +4 item with option (money pool)",
            he: "פריט +4 נוסף עם option (מאגר כסף)",
          },
          level: "+4",
          option: "≥1",
          note: {
            en: "Optional and not needed here — it would only help if the rate were value-based, but this combine is fixed at 60%.",
            he: "אופציונלי ולא נחוץ כאן — היה עוזר רק אם השיעור היה מבוסס-ערך, אך שילוב זה קבוע על 60%.",
          },
        },
      ],
      results: [
        { img: guideImg.wingElf, name: "Wings of Elf" },
        { img: guideImg.wingHeaven, name: "Wings of Heaven" },
        { img: guideImg.wingSatan, name: "Wings of Satan" },
        { img: guideImg.wingCurse, name: "Wing of Curse" },
      ],
    },
    {
      type: "results",
      title: { en: "Which Level 1 wing you get", he: "איזו כנף Level 1 מתקבלת" },
      items: [
        {
          img: guideImg.wingElf,
          name: "Wings of Elf",
          classes: { en: "Fairy Elf (FE)", he: "Fairy Elf (FE)" },
        },
        {
          img: guideImg.wingHeaven,
          name: "Wings of Heaven",
          classes: {
            en: "Dark Wizard (DW) · Magic Gladiator (MG)",
            he: "Dark Wizard (DW) · Magic Gladiator (MG)",
          },
        },
        {
          img: guideImg.wingSatan,
          name: "Wings of Satan",
          classes: {
            en: "Dark Knight (DK) · Magic Gladiator (MG)",
            he: "Dark Knight (DK) · Magic Gladiator (MG)",
          },
        },
        {
          img: guideImg.wingCurse,
          name: "Wing of Curse",
          classes: { en: "Summoner (SU)", he: "Summoner (SU)" },
        },
      ],
    },
    {
      type: "note",
      en: "The wing type is decided by your character class. Optional class talismans can force a specific wing type if you want to guarantee the result.",
      he: "סוג הכנף נקבע לפי מחלקת הדמות שלכם. Talisman ייעודי למחלקה יכול לכפות סוג כנף מסוים אם רוצים להבטיח את התוצאה.",
    },
  ],

  "wings-2": [
    {
      type: "h",
      en: "Second Wings (Level 2)",
      he: "כנפיים שניות (Level 2)",
    },
    {
      type: "p",
      en: "Level 2 wings use Loch's Feather. Dark Lord and Rage Fighter use the Monarch's Crest path instead for Capes — see the last section.",
      he: "כנפיים ברמה 2 משתמשות ב־Loch's Feather. Dark Lord ו־Rage Fighter משתמשים במסלול Monarch's Crest לקייפים — ראו את הקטע האחרון.",
    },
    chaosGoblinNpc(),
    ...luckVipNotes(),
    failWarn(),

    {
      type: "h",
      en: "Craft a Level 2 Wing",
      he: "יצירת כנף Level 2",
    },
    {
      type: "p",
      en: "Other classes (not DL/RF): place exactly one Jewel of Chaos, one Loch's Feather, and any Level 1 wing. Because the rate is fixed, you do NOT need an Excellent +4 wing for this combine.",
      he: "מחלקות אחרות (לא DL/RF): שימו בדיוק Jewel of Chaos אחד, Loch's Feather אחד, וכל כנף Level 1. מכיוון שהשיעור קבוע, אינכם צריכים כנף Excellent +4 לשילוב הזה.",
    },
    {
      type: "mix",
      title: { en: "Wings (Level 2)", he: "Wings (Level 2)" },
      zen: {
        en: "Fixed 5,000,000 — does not scale with rate, Luck, or account bonus.",
        he: "קבוע 5,000,000 — לא משתנה לפי שיעור, Luck או בונוס חשבון.",
      },
      rate: {
        en: "Base 60% for all account levels. Account bonus and Luck are applied together for the roll: Normal 60% · Bronze 65% · Silver 70% · Gold 75%, plus Talisman of Luck (max 100%).",
        he: "בסיס 60% לכל רמות החשבון. בונוס החשבון ו־Luck חלים יחד על הגלגול: Normal 60% · Bronze 65% · Silver 70% · Gold 75%, בתוספת Talisman of Luck (מקסימום 100%).",
      },
      fail: FAIL_CELL,
      ingredients: [
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×1" },
        {
          img: guideImg.lochFeather,
          name: "Loch's Feather",
          level: "Level 0",
          qty: "×1",
        },
        {
          name: {
            en: "Any Level 1 wing (Wings of Elf / Heaven / Satan / Curse)",
            he: "כל כנף Level 1 (Wings of Elf / Heaven / Satan / Curse)",
          },
          qty: "×1",
          note: {
            en: "The first wing you crafted earlier.",
            he: "הכנף הראשונה שיצרתם קודם.",
          },
        },
      ],
      results: [
        { img: guideImg.wingSpirits, name: "Wings of Spirits" },
        { img: guideImg.wingSoul, name: "Wings of Soul" },
        { img: guideImg.wingDragon, name: "Wings of Dragon" },
        { img: guideImg.wingDarkness, name: "Wings of Darkness" },
        { img: guideImg.wingDespair, name: "Wind of Despair" },
      ],
    },
    {
      type: "results",
      title: { en: "Which Level 2 wing you get", he: "איזו כנף Level 2 מתקבלת" },
      items: [
        {
          img: guideImg.wingSpirits,
          name: "Wings of Spirits",
          classes: { en: "Fairy Elf (FE)", he: "Fairy Elf (FE)" },
        },
        {
          img: guideImg.wingSoul,
          name: "Wings of Soul",
          classes: { en: "Dark Wizard (DW)", he: "Dark Wizard (DW)" },
        },
        {
          img: guideImg.wingDragon,
          name: "Wings of Dragon",
          classes: { en: "Dark Knight (DK)", he: "Dark Knight (DK)" },
        },
        {
          img: guideImg.wingDarkness,
          name: "Wings of Darkness",
          classes: { en: "Magic Gladiator (MG)", he: "Magic Gladiator (MG)" },
        },
        {
          img: guideImg.wingDespair,
          name: "Wind of Despair",
          classes: { en: "Summoner (SU)", he: "Summoner (SU)" },
        },
      ],
    },
    {
      type: "note",
      en: "The result is decided by your character class.",
      he: "התוצאה נקבעת לפי מחלקת הדמות שלכם.",
    },

    {
      type: "h",
      en: "Capes — Dark Lord & Rage Fighter",
      he: "קייפים — Dark Lord ו־Rage Fighter",
    },
    {
      type: "p",
      en: "Dark Lord and Rage Fighter use the Monarch's Crest path (not Loch's Feather) to make a cape instead of a Level 2 wing: DL → Cape of Lord, RF → Cape of Fighter. Same rates and fixed Zen as the Level 2 wing combine.",
      he: "Dark Lord ו־Rage Fighter משתמשים במסלול Monarch's Crest (לא Loch's Feather) ליצירת קייפ במקום כנף Level 2: DL → Cape of Lord, RF → Cape of Fighter. אותם שיעורים ו־Zen קבוע כמו שילוב כנף Level 2.",
    },
    {
      type: "mix",
      title: { en: "Cape (Level 2)", he: "Cape (Level 2)" },
      zen: {
        en: "Fixed 5,000,000 — same as Level 2 wings; does not scale with rate, Luck, or account bonus.",
        he: "קבוע 5,000,000 — כמו כנפי Level 2; לא משתנה לפי שיעור, Luck או בונוס חשבון.",
      },
      rate: {
        en: "Same as Level 2 wings — Base 60% all account levels; account bonus + Luck for the roll: Normal 60% · Bronze 65% · Silver 70% · Gold 75% (max 100%).",
        he: "כמו כנפי Level 2 — בסיס 60% לכל רמות החשבון; בונוס חשבון + Luck לגלגול: Normal 60% · Bronze 65% · Silver 70% · Gold 75% (מקסימום 100%).",
      },
      fail: FAIL_CELL,
      ingredients: [
        {
          img: guideImg.lochCrest,
          name: "Monarch's Crest",
          qty: "×1",
        },
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×1" },
        {
          name: {
            en: "Any Level 1 wing",
            he: "כל כנף Level 1",
          },
          qty: "×1",
        },
      ],
      results: [
        { img: guideImg.capeLord, name: "Cape of Lord" },
        { img: guideImg.capeFighter, name: "Cape of Fighter" },
      ],
    },
    {
      type: "results",
      title: { en: "Which cape you get", he: "איזה קייפ מתקבל" },
      items: [
        {
          img: guideImg.capeLord,
          name: "Cape of Lord",
          classes: { en: "Dark Lord (DL)", he: "Dark Lord (DL)" },
        },
        {
          img: guideImg.capeFighter,
          name: "Cape of Fighter",
          classes: { en: "Rage Fighter (RF)", he: "Rage Fighter (RF)" },
        },
      ],
    },
  ],

  "wings-3": [
    {
      type: "h",
      en: "Third Wings (Level 3)",
      he: "כנפיים שלישיות (Level 3)",
    },
    {
      type: "p",
      en: "Third wings and capes are the strongest tier and take multiple stages: craft a Condor Feather, obtain a Condor Flame, then combine both with your best gear to make the final wing or cape.",
      he: "כנפיים וקייפים ברמה 3 הם הדרגה החזקה ביותר ודורשים כמה שלבים: יצירת Condor Feather, השגת Condor Flame, ואז שילוב שניהם עם הציוד הטוב ביותר שלכם ליצירת הכנף או הקייפ הסופי.",
    },
    chaosGoblinNpc(),
    ...luckVipNotes(),
    failWarn(),

    {
      type: "timeline",
      steps: [
        {
          title: {
            en: "Stage 1 — Craft a Condor Feather",
            he: "שלב 1 — יצירת Condor Feather",
          },
          body: {
            en: "Combine jewels with a +9 Level 2 wing / cape and an Ancient item in the Chaos Machine.",
            he: "שלבו תכשיטים עם כנף / קייפ Level 2 בדרגה +9 ופריט Ancient ב־Chaos Machine.",
          },
          img: guideImg.condorFeather,
        },
        {
          title: {
            en: "Stage 2 — Obtain a Condor Flame",
            he: "שלב 2 — השגת Condor Flame",
          },
          body: {
            en: "Not crafted in the Chaos Machine. Drops in Balgass' Barracks from Barracks monsters (e.g. Balram, Death Spirit, Soram) while you are on that map — rare; or trade from other players.",
            he: "לא נוצר ב־Chaos Machine. נופל ב־Balgass' Barracks ממפלצות ה־Barracks (למשל Balram, Death Spirit, Soram) בזמן שאתם על המפה — נדיר; או מסחר משחקנים אחרים.",
          },
          img: guideImg.condorFlame,
        },
        {
          title: {
            en: "Stage 3 — Craft the Third Wing / Cape",
            he: "שלב 3 — יצירת הכנף / הקייפ השלישי",
          },
          body: {
            en: "Combine the Condor Feather, Condor Flame, jewels and an Excellent item to create your final wing or cape.",
            he: "שלבו את ה־Condor Feather, ה־Condor Flame, תכשיטים ופריט Excellent כדי ליצור את הכנף או הקייפ הסופי.",
          },
          img: guideImg.wingStorm,
        },
      ],
    },

    {
      type: "h",
      en: "Stage 1 — Craft the Condor Feather",
      he: "שלב 1 — יצירת ה־Condor Feather",
    },
    {
      type: "p",
      en: "Combine the jewels below with a +9 Level 2 wing or cape (Option ≥ 1) and an Ancient (Set) item (Level ≥ 7, Option ≥ 1). Higher total item value raises the rate.",
      he: "שלבו את התכשיטים למטה עם כנף או קייפ Level 2 בדרגה +9 (Option ≥ 1) ופריט Ancient (Set) (Level ≥ 7, Option ≥ 1). ערך פריטים כולל גבוה יותר מעלה את השיעור.",
    },
    {
      type: "mix",
      title: { en: "Condor Feather", he: "Condor Feather" },
      zen: {
        en: "Zen = (rate after Luck) × 200,000 before account bonus. Account bonus then applies to the roll only (does not change Zen).",
        he: "Zen = (שיעור אחרי Luck) × 200,000 לפני בונוס החשבון. בונוס החשבון חל אחר כך על הגלגול בלבד (לא משנה את ה־Zen).",
      },
      rate: {
        en: "Base = (total item money value ÷ 300,000) + 1, soft-capped around 60%. Talisman of Luck adds (affects Zen), then account bonus Normal +0 · Bronze +5 · Silver +10 · Gold +15 applies to the roll only.",
        he: "בסיס = (ערך הכסף הכולל של הפריטים ÷ 300,000) + 1, עם תקרה רכה סביב 60%. Talisman of Luck מוסיף (משפיע על Zen), ואז בונוס החשבון Normal +0 · Bronze +5 · Silver +10 · Gold +15 חל על הגלגול בלבד.",
      },
      fail: FAIL_CELL,
      ingredients: [
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×1" },
        { img: guideImg.jewelCreation, name: "Jewel of Creation", qty: "×1" },
        {
          img: guideImg.jewelSoulBundle,
          name: "Bundled Jewel of Soul",
          level: "Level 0",
          qty: "×1",
        },
        {
          name: {
            en: "Level 2 wing or cape",
            he: "כנף או קייפ Level 2",
          },
          level: "+9",
          option: "≥1",
          qty: "×1",
        },
        {
          name: {
            en: "Ancient (Set) item",
            he: "פריט Ancient (Set)",
          },
          ancient: true,
          level: "≥7",
          option: "≥1",
          qty: "×1",
        },
      ],
      result: { img: guideImg.condorFeather, name: "Condor Feather" },
    },

    {
      type: "h",
      en: "Stage 2 — Obtain the Condor Flame",
      he: "שלב 2 — השגת ה־Condor Flame",
    },
    {
      type: "p",
      en: "The Condor Flame is not crafted in the Chaos Machine. It drops in Balgass' Barracks from Barracks monsters (for example Balram, Death Spirit, Soram) while you are on that map. The drop is rare — no percentage is published here. You can also obtain it from other players (trade).",
      he: "ה־Condor Flame לא נוצר ב־Chaos Machine. הוא נופל ב־Balgass' Barracks ממפלצות ה־Barracks (למשל Balram, Death Spirit, Soram) בזמן שאתם על המפה. הדרופ נדיר — אחוז לא מפורסם כאן. אפשר גם להשיג משחקנים אחרים (מסחר).",
    },
    {
      type: "itemcard",
      img: guideImg.condorFlame,
      name: "Condor Flame",
      note: {
        en: "Not crafted. Drops in Balgass' Barracks (e.g. Balram, Death Spirit, Soram) while on that map — rare. Or trade from other players.",
        he: "לא נוצר בקראפט. נופל ב־Balgass' Barracks (למשל Balram, Death Spirit, Soram) בזמן שעל המפה — נדיר. או מסחר משחקנים אחרים.",
      },
    },

    {
      type: "h",
      en: "Stage 3 — Craft the Third Wing / Cape",
      he: "שלב 3 — יצירת הכנף / הקייפ השלישי",
    },
    {
      type: "p",
      en: "Combine both Condor materials with the jewels below and an Excellent item (Level ≥ 9, Option ≥ 1). Higher total item value raises the rate.",
      he: "שלבו את שני חומרי ה־Condor עם התכשיטים למטה ופריט Excellent (Level ≥ 9, Option ≥ 1). ערך פריטים כולל גבוה יותר מעלה את השיעור.",
    },
    {
      type: "mix",
      title: {
        en: "Third Wing / Cape",
        he: "כנף / קייפ שלישי",
      },
      zen: {
        en: "Zen = (rate after Luck) × 200,000 before account bonus. Account bonus then applies to the roll only (does not change Zen).",
        he: "Zen = (שיעור אחרי Luck) × 200,000 לפני בונוס החשבון. בונוס החשבון חל אחר כך על הגלגול בלבד (לא משנה את ה־Zen).",
      },
      rate: {
        en: "Base = (total item money value ÷ 3,000,000) + 1, soft-capped around 40%. Talisman of Luck adds (affects Zen), then account bonus Normal +0 · Bronze +5 · Silver +10 · Gold +15 applies to the roll only.",
        he: "בסיס = (ערך הכסף הכולל של הפריטים ÷ 3,000,000) + 1, עם תקרה רכה סביב 40%. Talisman of Luck מוסיף (משפיע על Zen), ואז בונוס החשבון Normal +0 · Bronze +5 · Silver +10 · Gold +15 חל על הגלגול בלבד.",
      },
      fail: FAIL_CELL,
      ingredients: [
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×1" },
        { img: guideImg.jewelCreation, name: "Jewel of Creation", qty: "×1" },
        {
          img: guideImg.jewelSoulBundle,
          name: "Bundled Jewel of Soul",
          level: "Level 0",
          qty: "×1",
        },
        {
          img: guideImg.jewelBlessBundle,
          name: "Bundled Jewel of Bless",
          level: "Level 0",
          qty: "×1",
        },
        { img: guideImg.condorFlame, name: "Condor Flame", qty: "×1" },
        { img: guideImg.condorFeather, name: "Condor Feather", qty: "×1" },
        {
          name: {
            en: "Excellent item",
            he: "פריט Excellent",
          },
          excellent: true,
          level: "≥9",
          option: "≥1",
          qty: "×1",
        },
      ],
      results: [
        { img: guideImg.wingStorm, name: "Wing of Storm" },
        { img: guideImg.wingEternal, name: "Wing of Eternal" },
        { img: guideImg.wingIllusion, name: "Wing of Illusion" },
        { img: guideImg.wingRuin, name: "Wing of Ruin" },
        { img: guideImg.capeEmperor, name: "Emperor's Cape" },
        { img: guideImg.wingDimension, name: "Wing of Dimension" },
        { img: guideImg.capeOverrule, name: "Cape of Overrule" },
      ],
    },
    {
      type: "results",
      title: {
        en: "Which third wing / cape you get",
        he: "איזו כנף / קייפ שלישי מתקבל",
      },
      items: [
        {
          img: guideImg.wingStorm,
          name: "Wing of Storm",
          classes: { en: "Dark Knight (DK)", he: "Dark Knight (DK)" },
        },
        {
          img: guideImg.wingEternal,
          name: "Wing of Eternal",
          classes: { en: "Dark Wizard (DW)", he: "Dark Wizard (DW)" },
        },
        {
          img: guideImg.wingIllusion,
          name: "Wing of Illusion",
          classes: { en: "Fairy Elf (FE)", he: "Fairy Elf (FE)" },
        },
        {
          img: guideImg.wingRuin,
          name: "Wing of Ruin",
          classes: { en: "Magic Gladiator (MG)", he: "Magic Gladiator (MG)" },
        },
        {
          img: guideImg.capeEmperor,
          name: "Emperor's Cape",
          classes: { en: "Dark Lord (DL)", he: "Dark Lord (DL)" },
        },
        {
          img: guideImg.wingDimension,
          name: "Wing of Dimension",
          classes: { en: "Summoner (SU)", he: "Summoner (SU)" },
        },
        {
          img: guideImg.capeOverrule,
          name: "Cape of Overrule",
          classes: { en: "Rage Fighter (RF)", he: "Rage Fighter (RF)" },
        },
      ],
    },
    {
      type: "note",
      en: "The final wing or cape is decided by your character class.",
      he: "הכנף או הקייפ הסופי נקבע לפי מחלקת הדמות שלכם.",
    },
  ],
};

export const wingsTopicIds = Object.keys(guideWingsContent);

export default guideWingsContent;
